import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useSendOTPMutation, useVerifyOTPMutation } from "@/hooks/auth";
import { toast } from "react-toastify";

const labels = {
  registerTitle: "Registracija",
  emailStepDescription: "Unesite svoj email kako biste započeli registraciju.",
  otpStepDescription: "Unesite kod koji ste primili na svoj email.",
  emailLabel: "Email",
  otpLabel: "Kod za potvrdu",
  submitEmail: "Pošalji kod",
  verifyOtp: "Potvrdi kod",
  universalErrorMessage: "Došlo je do pogreške. Molimo pokušajte ponovno.",
  notificationOtpSent: "Kod za potvrdu je poslan na vaš email.",
  notificationOtpVerified: "Kod za potvrdu je uspješno verificiran.",
};

const emailSchema = z.object({
  email: z.string().email("Unesite važeću email adresu"),
});

const otpSchema = z.object({
  otp: z.string().length(6, "Kod mora imati 6 znamenki"),
});

type EmailFormValues = z.infer<typeof emailSchema>;
type OtpFormValues = z.infer<typeof otpSchema>;

export default function Register() {
  const sendOTPMutation = useSendOTPMutation();
  const verifyOTPMutation = useVerifyOTPMutation();

  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    setError,
    formState: { errors: emailErrors },
  } = useForm<EmailFormValues>({ resolver: zodResolver(emailSchema) });

  const {
    register: registerOtp,
    handleSubmit: handleOtpSubmit,
    setError: setOtpError, // Access setError for the OTP form
    formState: { errors: otpErrors },
  } = useForm<OtpFormValues>({ resolver: zodResolver(otpSchema) });

  const sendOtp = async (data: EmailFormValues) => {
    sendOTPMutation.mutate(data.email, {
      onSuccess: () => {
        toast.success(labels.notificationOtpSent);
        setEmail(data.email);
        setStep("otp");
      },
      onError: (error) => {
        toast.error(labels.universalErrorMessage);

        setError("email", {
          type: "manual",
          message: error.message || labels.universalErrorMessage,
        });
      },
    });
  };

  const handleVerifyOtp = (data: OtpFormValues) => {
    verifyOTPMutation.mutate(
      { email, otp: data.otp },
      {
        onSuccess: async () => {
          toast.success(labels.notificationOtpVerified);
        },
        onError: (error: Error) => {
          toast.error(labels.universalErrorMessage);
          setOtpError("otp", {
            type: "manual",
            message: error.message,
          });
        },
      }
    );
  };

  return (
    <div className="flex min-h-[95vh] w-full items-center justify-center">
      <div className="w-full max-w-sm">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">{labels.registerTitle}</CardTitle>
            <p>
              {step === "email"
                ? labels.emailStepDescription
                : labels.otpStepDescription}
            </p>
          </CardHeader>
          <CardContent>
            {step === "email" && (
              <form onSubmit={handleEmailSubmit(sendOtp)} noValidate>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">{labels.emailLabel}</Label>
                    <Input
                      id="email"
                      type="email"
                      {...registerEmail("email")}
                    />
                    {emailErrors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {emailErrors.email.message}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    isLoading={sendOTPMutation.isPending}
                  >
                    {labels.submitEmail}
                  </Button>
                </div>
              </form>
            )}
            {step === "otp" && (
              <form onSubmit={handleOtpSubmit(handleVerifyOtp)} noValidate>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="otp">{labels.otpLabel}</Label>
                    <Input id="otp" type="text" {...registerOtp("otp")} />
                    {otpErrors.otp && (
                      <p className="text-red-500 text-sm mt-1">
                        {otpErrors.otp.message}
                      </p>
                    )}
                  </div>
                  <Button type="submit" className="w-full">
                    {labels.verifyOtp}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
